import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { supportedLanguages } from "../../locales";
import { OG_LOCALES, localizePath, normalizeLanguage } from "../../lib/localeRoutes";

type FAQItem = { question: string; answer: string };

interface SEOProps {
  route?: keyof typeof routeData;
  section?: string;
  path?: string;
  image?: string;
  faqs?: FAQItem[];
  nearbyPlaces?: Array<{ name: string; google_maps: string }>;
}

/* =========================
   SITE CONSTANTS
========================= */

const SITE_URL = (import.meta.env.VITE_SITE_URL || "https://darzaytounbhalil.com").replace(/\/+$/, "");
const BRAND = "Dar Zaytoun";
const BRAND_FULL = "Dar Zaytoun Bhalil";

const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-cover.jpg`;

function joinUrl(base: string, path: string) {
  const b = base.replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  return p ? `${b}/${p}` : `${b}/`;
}

function canonicalFor(pathname: string) {
  return joinUrl(SITE_URL, pathname);
}

/* =========================
   ROUTE SEO DATA
========================= */

const routeData = {
  home: {
    path: "/",
    title: `${BRAND} Bhalil | Boutique Guesthouse Near Fez, Morocco`,
    description:
      "Discover Dar Zaytoun, a traditional boutique guesthouse in Bhalil village, just 30 minutes from Fez. Swimming pool, mountain views, authentic Moroccan hospitality, cave house tours, and easy day trips to Fez, Sefrou, Ifrane & Meknes.",
    keywords:
      "Dar Zaytoun Bhalil, guesthouse Bhalil, B&B near Fez, accommodation near Sefrou, boutique guesthouse Morocco, traditional Moroccan guesthouse, stay in Bhalil, Bhalil cave houses, Sefrou Cherry Festival accommodation, guesthouse near Fes, authentic Moroccan experience, Morocco countryside accommodation, eco guesthouse Morocco, Middle Atlas accommodation",
    // Home relies on the canonical inline @graph baked into index.html — no duplicate schema injected.
    jsonLd: null,
  },

  bhalil: {
    path: "/bhalil",
    title: `About Bhalil – Cave Houses & Berber Village Near Fez | ${BRAND_FULL}`,
    description:
      "Discover Bhalil, Morocco's hidden gem near Fez — an ancient Berber village famous for its unique cave houses (maisons troglodytes), artisan workshops, and authentic rural life in the Middle Atlas. Stay at Dar Zaytoun and explore from here.",
    keywords:
      "Bhalil Morocco, Bhalil village, Bhalil cave houses, maisons troglodytes Bhalil, near Fez village, Fes-Meknes, authentic Morocco, Berber village Morocco, Middle Atlas, cave dwellings Morocco",
    // prerender.mjs already writes this route's TouristDestination schema
    // directly into <head> at build time — a client copy would only conflict.
    jsonLd: null,
  },

  rooms: {
    path: "/rooms",
    title: `Rooms & Suites – Traditional Moroccan Accommodation | ${BRAND_FULL}`,
    description:
      "Choose from 5 elegantly decorated rooms at Dar Zaytoun Bhalil. Traditional Moroccan décor, mountain views, garden views, swimming pool access, breakfast included. From €80/night near Fez. Book the entire rooms for groups up to 12.",
    keywords:
      "Dar Zaytoun rooms, Bhalil accommodation, guesthouse rooms near Fez, Moroccan B&B rooms, stay in Bhalil, boutique room Morocco, traditional Moroccan room, Suite Luna mountain view, Chambre Amande, Chambre Murier",
    // prerender.mjs's LodgingBusiness block for this route is the accurate,
    // complete one (5 rooms incl. Suite Luna) — this used to duplicate it with
    // a stale 4-room version sharing the same @id. Do not re-add a jsonLd here;
    // fix data in scripts/prerender.mjs instead.
    jsonLd: null,
  },

  services: {
    path: "/services",
    title: `Services & Amenities – What's Included | ${BRAND_FULL}`,
    description:
      "Enjoy free WiFi, swimming pool, traditional Moroccan breakfast, rooftop terrace, private airport transfer from Fez (€35), free parking, and authentic hospitality at Dar Zaytoun in Bhalil.",
    keywords:
      "Dar Zaytoun amenities, guesthouse services Morocco, breakfast included B&B, WiFi Bhalil, stay near Fez services, swimming pool Bhalil, rooftop terrace Morocco, airport transfer Fez, private transfer Morocco",
    // See rooms: prerender.mjs already emits this route's LodgingBusiness block.
    jsonLd: null,
  },

  gallery: {
    path: "/gallery",
    title: `Photo Gallery – Rooms, Pool, Garden & Mountain Views | ${BRAND_FULL}`,
    description:
      "Browse photos of Dar Zaytoun in Bhalil — elegant rooms, swimming pool, lush garden, rooftop terrace, Moroccan décor, mountain views, and traditional breakfast. See why guests love staying here near Fez.",
    keywords:
      "Dar Zaytoun gallery, Bhalil guesthouse photos, Morocco B&B images, swimming pool Morocco, Moroccan garden, mountain view room Morocco, traditional decor Morocco, Bhalil village photos",
    jsonLd: null,
  },

  nearby: {
    path: "/nearby",
    title: `Nearby Places & Day Trips from Bhalil | Fez, Sefrou, Ifrane, Meknes | ${BRAND_FULL}`,
    description:
      "Discover the best nearby places from Dar Zaytoun in Bhalil: Fez Medina (UNESCO), Sefrou Cherry Festival, Ifrane alpine resort, Azrou Cedar Forest, Meknes, Moulay Idriss, and Volubilis Roman ruins. All within 2 hours.",
    keywords:
      "nearby places Bhalil, day trips from Fez, Fez Medina, Sefrou Cherry Festival, Ifrane Morocco, Azrou cedar forest, Meknes imperial city, Moulay Idriss, Volubilis UNESCO, day trips near Fez, excursions from Bhalil",
    jsonLd: null,
  },

  reviews: {
    path: "/reviews",
    title: `Guest Reviews & Testimonials – 5/5 | ${BRAND_FULL}`,
    description:
      "Read authentic guest reviews for Dar Zaytoun in Bhalil near Fez — rated 5/5 on Google and Airbnb. Guests love the garden with old olive trees, the pool, the calm, and the warm welcome from the hosts.",
    keywords:
      "Dar Zaytoun reviews, Bhalil guesthouse reviews, Morocco B&B reviews, stay near Fez testimonials, authentic Moroccan guesthouse review, Bhalil accommodation review",
    // Reviews/AggregateRating are covered by the master @graph in index.html;
    // a second copy here previously conflicted with it (7 vs 23 review counts).
    jsonLd: null,
  },

  findus: {
    path: "/findus",
    title: `How to Get to Bhalil – Directions to Dar Zaytoun | Near Fez, Morocco`,
    description:
      "Find Dar Zaytoun in Bhalil village, just 30 minutes from Fez. Get directions, Google Maps link, and travel tips. Private airport transfer from Fez-Saïss Airport available for €35. By car: take N8 south, then R413 to Bhalil.",
    keywords:
      "Dar Zaytoun location, directions to Bhalil, how to get to Bhalil from Fez, guesthouse near Fez map, Bhalil GPS, Fez to Bhalil drive, airport transfer Fez Morocco, how to reach Bhalil Morocco",
    jsonLd: null,
  },

  contact: {
    path: "/contact",
    title: `Contact & Direct Booking | ${BRAND_FULL} – Near Fez, Morocco`,
    description:
      "Contact Dar Zaytoun in Bhalil near Fez for direct bookings and inquiries. WhatsApp: +212 770 780 837. Email: DarZaytounBhalil@gmail.com. We speak English, French and Arabic. Best prices guaranteed on direct bookings.",
    keywords:
      "contact Dar Zaytoun, booking Bhalil guesthouse, B&B near Fez contact, reservations Dar Zaytoun, WhatsApp Dar Zaytoun, direct booking Morocco guesthouse",
    jsonLd: null,
  },

  bio: {
    path: "/bio",
    title: `Bio & Quick Links | ${BRAND_FULL} – Contact, WhatsApp & Maps`,
    description:
      "Quickly access Dar Zaytoun's WhatsApp, phone, email, map location, Airbnb listing, Booking.com, Google Maps, and social media links from one page.",
    keywords:
      "Dar Zaytoun contact, Dar Zaytoun WhatsApp, Dar Zaytoun map, Dar Zaytoun Instagram, Dar Zaytoun Airbnb, Dar Zaytoun Booking.com",
    jsonLd: null,
  },
} as const;

const sectionToRoute: Record<string, keyof typeof routeData> = {
  "/": "home", home: "home", "/home": "home",
  bhalil: "bhalil", "/bhalil": "bhalil",
  rooms: "rooms", "/rooms": "rooms",
  services: "services", "/services": "services",
  gallery: "gallery", "/gallery": "gallery",
  nearby: "nearby", "/nearby": "nearby",
  reviews: "reviews", "/reviews": "reviews",
  findus: "findus", "/findus": "findus",
  contact: "contact", "/contact": "contact",
  bio: "bio", "/bio": "bio",
};

export default function SEO({ route, section, path, image, faqs, nearbyPlaces }: SEOProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.language);
  const resolvedRoute: keyof typeof routeData =
    route || (section ? sectionToRoute[section] : undefined) || "home";

  const data = routeData[resolvedRoute] || routeData.home;
  const pathname = typeof path === "string" ? path : data.path;
  // Canonical and hreflang must reflect the language prefix, or every language
  // claims the same URL and Google discards the whole annotation set.
  const canonicalUrl = canonicalFor(localizePath(pathname, lang));

  const localizedTitle = t(`seo.${resolvedRoute}.title`);
  const localizedDescription = t(`seo.${resolvedRoute}.description`);
  const localizedImageAlt = t("seo.imageAlt");

  const finalImage =
    image && image.startsWith("http")
      ? image
      : image
      ? joinUrl(SITE_URL, image)
      : DEFAULT_OG_IMAGE;

  /* Breadcrumb schema */
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: canonicalFor("/") },
      ...(resolvedRoute !== "home"
        ? [
            {
              "@type": "ListItem",
              position: 2,
              name:
                resolvedRoute === "findus"
                  ? "Find Us"
                  : resolvedRoute.charAt(0).toUpperCase() + resolvedRoute.slice(1),
              item: canonicalUrl,
            },
          ]
        : []),
    ],
  };

  /* FAQ schema — prerender.mjs already writes this statically for the home
     route (the only one where the FAQ content is actually about), so skip it
     there to avoid a duplicate node once React hydrates. */
  const faqSchema =
    resolvedRoute !== "home" && faqs && faqs.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }
      : null;

  /* Nearby ItemList schema */
  const nearbySchema =
    resolvedRoute === "nearby" && nearbyPlaces && nearbyPlaces.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `Day Trips & Nearby Places from ${BRAND_FULL} Bhalil`,
          description: "Top nearby attractions and day trip destinations from Dar Zaytoun Bhalil near Fez, Morocco",
          itemListElement: nearbyPlaces.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.name,
            url: p.google_maps,
          })),
        }
      : null;

  return (
    <Helmet>
      {/* Core */}
      <title>{localizedTitle}</title>
      <meta name="description" content={localizedDescription} />
      <meta name="keywords" content={data.keywords} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      <meta name="author" content={BRAND_FULL} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang */}
      {supportedLanguages.map((code) => (
        <link
          key={code}
          rel="alternate"
          hrefLang={code}
          href={canonicalFor(localizePath(pathname, code))}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonicalFor(localizePath(pathname, "en"))} />

      {/* Geo / Local SEO */}
      <meta name="geo.region" content="MA-05" />
      <meta name="geo.placename" content="Bhalil, Sefrou Province, Fès-Meknès, Morocco" />
      <meta name="geo.position" content="33.8687974;-4.8775435" />
      <meta name="ICBM" content="33.8687974, -4.8775435" />

      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={localizedTitle} />
      <meta property="og:description" content={localizedDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={localizedImageAlt} />
      <meta property="og:locale" content={OG_LOCALES[lang]} />
      {supportedLanguages
        .filter((code) => code !== lang)
        .map((code) => (
          <meta key={code} property="og:locale:alternate" content={OG_LOCALES[code]} />
        ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={localizedTitle} />
      <meta name="twitter:description" content={localizedDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={localizedImageAlt} />

      {/* JSON-LD: Main page schema (when defined for sub-routes) */}
      {data.jsonLd ? <script type="application/ld+json">{JSON.stringify(data.jsonLd)}</script> : null}

      {/* JSON-LD: Breadcrumb */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      {/* JSON-LD: FAQ (when provided) */}
      {faqSchema ? (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      ) : null}

      {/* JSON-LD: Nearby ItemList (when on nearby section with data) */}
      {nearbySchema ? (
        <script type="application/ld+json">{JSON.stringify(nearbySchema)}</script>
      ) : null}
    </Helmet>
  );
}
